<script>
import CSubscription from "@/components/container/CSubscription.vue"
import PHeading from "@/components/presentational/PHeading.vue"
import CRegion from "@/components/container/CRegion.vue"
import CPricing from "@/components/container/CPricing.vue"
import CInstance from "@/components/container/CInstance.vue"
import PInstanceProgress from "@/components/presentational/PInstanceProgress.vue"
import PFetchingIndicator from "@/components/presentational/PFetchingIndicator.vue"
import { usePermissions } from "@/composables/usePermissions.js"
import { isNonProd } from "@/stage.js"
import PTrialStatusBar from "@/components/presentational/PTrialStatusBar.vue"
import { RCA_CHECKOUT_ENABLED, isSalesforceSubscription } from "@/store/modules/subscription.js"

const options = { style: "currency", minimumFractionDigits: 2, maximumFractionDigits: 2 }
const priceFormats = {
  EUR: new Intl.NumberFormat(undefined, {
    ...options,
    currency: "EUR",
  }),
  USD: new Intl.NumberFormat(undefined, {
    ...options,
    currency: "USD",
  }),
}

export default {
  name: "Overview",
  components: {
    PTrialStatusBar,
    PFetchingIndicator,
    PInstanceProgress,
    CInstance,
    CRegion,
    CPricing,
    PHeading,
    CSubscription,
  },
  setup() {
    const { hasPermission } = usePermissions()

    return {
      hasPermission,
    }
  },
  data() {
    return {
      dialog: false,
      selectedOption: null,
      // this controls if the backend will create an EC2 instance
      isDev: isNonProd,
      // tracks the programmatic routing state while the guard checks eligibility
      isNavigatingToTrial: false,
      isRcaCheckoutEnabled: RCA_CHECKOUT_ENABLED,
    }
  },
  methods: {
    async navigateToTrial() {
      this.isNavigatingToTrial = true
      try {
        await this.$router.push("/start-trial")
      }
      finally {
        this.isNavigatingToTrial = false
      }
    },
    cardContent({ sub, plan, timezone, regions }) {
      const common = [
        {
          overline: "prtg domain",
          underline: sub.fqdn,
        },
        {
          overline: "prtg timezone",
          underline: timezone.DisplayName,
        },
        {
          overline: "prtg server region",
          underline: regions[sub.region]?.name,
        },
      ]
      return {
        in_trial: [
          ...common,
          {
            overline: "pricing plan",
            underline: "Trial Plan",
          },
          {
            overline: "subscription status",
            underline: "trial active",
            cssClasses: "text-capitalize",
          },
        ],
        active: [
          ...common,
          {
            overline: "pricing plan",
            underline: `${this.getFormattedPlanName(plan)}`,
            cssClasses: "text-capitalize",
          },
          {
            overline: "subscription status",
            underline: "active",
            cssClasses: "text-capitalize",
          },
          {
            overline: sub.plannedRenewalPrice ? "price at next renewal" : "next subscription payment",
            underline: sub.plannedRenewalPrice
              ? `${(priceFormats[sub.currency] || priceFormats.EUR).format(sub.plannedRenewalPrice / 100)} on ${sub.nextPayment}`
              : sub.nextPayment,
          },
        ],
        non_renewing: [
          ...common,
          {
            overline: "pricing plan",
            underline: `${this.getFormattedPlanName(plan)}`,
            cssClasses: "text-capitalize",
          },
          {
            overline: "subscription status",
            underline: "non-renewing",
            cssClasses: "text-capitalize",
          },
          {
            overline: "subscription ends",
            underline: sub.contractEndDate,
            cssClasses: "warning--text",
          },
        ],
      }[sub.subscriptionStatus]
      // A status with no branch would return undefined and break the v-for that renders this.
      // Only two ever reach here today because the backend filters the list, so this exists for
      // the next status somebody adds rather than for anything reachable now.
      ?? common
    },
    // One button for both checkouts; which one opens is never the customer's choice (PPHM-2442).
    buySubscription(trialSubscription) {
      if (this.isRcaCheckoutEnabled) {
        // The Salesforce checkout works out for itself whether this converts a Salesforce trial or is
        // an additional purchase. A Chargebee trial is upgraded from its own card, not from here.
        this.$router.push("/checkout")
      }
      else {
        this.openUpgradeTrialDialogIfNeeded(trialSubscription)
      }
    },
    openUpgradeTrialDialogIfNeeded(trialSubscription) {
      if (trialSubscription) {
        this.selectedOption = null
        this.dialog = true
      }
      else {
        // There is no trial so navigate to /new
        this.$router.push("/new")
      }
    },
    // A trial is upgraded in the checkout of the backend that holds it: a Salesforce trial in the
    // Salesforce checkout, a Chargebee trial in the Chargebee plan change on its subscription page —
    // also after the switch is on, since the Salesforce checkout cannot convert a Chargebee trial.
    upgradeTrial(trialSubscription) {
      this.$router.push(this.isRcaCheckoutEnabled && isSalesforceSubscription(trialSubscription.id)
        ? "/checkout"
        : `/manage/${trialSubscription.id}/subscription`)
    },
    confirmDialog(trialSubscription) {
      this.dialog = false
      if (this.selectedOption === "upgrade") {
        this.upgradeTrial(trialSubscription)
      }
      else {
        this.$router.push("/new")
      }
    },
    getFormattedPlanName(plan) {
      if (!plan.name) return "Unavailable"
      const periodUnit = plan.period > 1 ? plan.period + " " + plan.periodUnit + "s" : plan.periodUnit
      return plan.price ? `${plan.name} (${plan.price} / ${periodUnit})` : plan.name
    },
  },
}
</script>

<template>
  <v-container>
    <p-heading disable-navigation>
      <template #icon>
        $mdiViewDashboard
      </template>
      subscription overview
      <v-spacer />
      <c-subscription
        v-slot="{
          trialSubscription,
          canHaveTrial,
          isFetching
        }"
      >
        <v-row
          v-if="!isFetching"
          class="align-right justify-end"
        >
          <v-col cols="auto">
            <v-btn
              v-if="canHaveTrial"
              data-add-trial
              size="small"
              color="light"
              variant="tonal"
              :loading="isNavigatingToTrial"
              @click="navigateToTrial"
            >
              Start Free Trial
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn
              data-add-or-upgrade
              size="small"
              @click="buySubscription(trialSubscription)"
            >
              Buy Subscription
            </v-btn>
            <v-dialog
              v-model="dialog"
              max-width="750"
            >
              <v-card>
                <v-card-item>
                  <v-card-title class="text-uppercase">
                    Buy subscription
                  </v-card-title>
                </v-card-item>
                <v-card-text>
                  <v-radio-group v-model="selectedOption">
                    <v-radio
                      value="upgrade"
                      label="Upgrade trial"
                    />
                    <div class="text-medium-emphasis ml-10 mt-n2 mb-3">
                      Update your existing trial instance to paid subscription
                    </div>
                    <v-radio
                      value="new"
                      label="Buy new instance"
                    />
                    <div class="text-medium-emphasis ml-10 mt-n2">
                      Purchase a new PRTG Hosted Monitor instance
                    </div>
                  </v-radio-group>
                </v-card-text>
                <v-divider />
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    variant="text"
                    @click="dialog = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="primary"
                    variant="elevated"
                    :disabled="!selectedOption"
                    @click="confirmDialog(trialSubscription)"
                  >
                    Confirm
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
        </v-row>
      </c-subscription>
    </p-heading>

    <c-subscription
      v-slot="{
        subscriptions,
        isFetching,
        hasSubscriptions,
        isLoading: subscriptionLoading
      }"
    >
      <p-fetching-indicator :show="isFetching">
        <div v-if="hasSubscriptions">
          <c-subscription
            v-for="sub in subscriptions"
            :id="sub.id"
            :key="sub.id"
            v-slot="{ isFailed, isComplete, inProgress, inUnknown, isBlockedSanctionReview }"
          >
            <c-region v-slot="{ regions, isFetching: fetchingRegion }">
              <c-pricing
                v-slot="{ currentPricingPlan: plan, isFetching: fetchingPricingPlans } "
                :active-plan-id="sub.planId"
                :active-subscription="sub"
              >
                <c-instance
                  v-slot="{ timezone, tzIsLoading: loadingTimezone }"
                  :stack-id="sub.stackId"
                  :region="sub.region"
                >
                  <v-card
                    class="mt-4"
                    :loading="loadingTimezone || fetchingPricingPlans || fetchingRegion || inProgress ? '#fff' : false"
                    :disabled="isBlockedSanctionReview"
                    data-test-sub-card
                  >
                    <p-instance-progress
                      :complete="isComplete"
                      :ongoing="inProgress"
                      :failed="isFailed"
                      :is-blocked-sanction-review="isBlockedSanctionReview"
                    />
                    <v-expand-transition>
                      <v-alert
                        v-if="inProgress || inUnknown"
                        type="info"
                        class="my-2 mx-4"
                      >
                        It may take a while until your PRTG is ready.
                      </v-alert>
                    </v-expand-transition>
                    <v-expand-transition>
                      <v-alert
                        v-if="isBlockedSanctionReview"
                        type="warning"
                        variant="tonal"
                        class="my-2 mx-4"
                        data-test="sanction-blocked-alert"
                      >
                        Your instance provisioning is on hold pending internal confirmation.
                        Our support team will contact you within 24 hours.
                        No action is required from you.
                      </v-alert>
                    </v-expand-transition>
                    <v-card-text
                      v-for="({ overline, underline, cssClasses }, i) in cardContent({ sub, plan, timezone, regions })"
                      :key="`${sub.id}-${i}`"
                      class="py-2"
                    >
                      <v-divider
                        v-if="i > 0"
                        class="mb-2"
                      />
                      <div :class="[cssClasses, 'text-body-medium', 'text-info', 'text-uppercase']">
                        {{ overline }}
                      </div>
                      <div
                        :class="[cssClasses, 'text-body-large', {
                          'loading': loadingTimezone || fetchingPricingPlans || fetchingRegion
                        }]"
                      >
                        {{ loadingTimezone || fetchingPricingPlans || fetchingRegion ? '' : underline }}
                      </div>
                    </v-card-text>
                    <template v-if="sub.isTrial">
                      <v-divider />
                      <p-trial-status-bar
                        :days-left="sub.trialDaysLeft"
                        :ends="sub.expiresAt"
                        :started="sub.trialStart ?? sub.createdAt"
                      />
                    </template>
                    <v-divider />
                    <v-card-actions>
                      <v-btn
                        :href="`https://${sub.fqdn}`"
                        :disabled="inProgress || inUnknown || isBlockedSanctionReview"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-open-prtg
                      >
                        open prtg
                      </v-btn>
                      <v-btn
                        v-if="sub.isTrial && hasPermission('manageSubscription', sub.id)"
                        :disabled="inProgress || inUnknown || isBlockedSanctionReview"
                        data-upgrade-trial
                        @click="upgradeTrial(sub)"
                      >
                        upgrade trial
                      </v-btn>
                      <v-btn
                        v-if="hasPermission('manageInstance', sub.id) || (hasPermission('manageSubscription', sub.id) && !sub.hasPartner)"
                        :disabled="inProgress || inUnknown || isBlockedSanctionReview"
                        append-icon="$mdiChevronDown"
                        data-manage
                      >
                        manage
                        <v-menu activator="parent">
                          <v-list>
                            <v-list-subheader class="text-uppercase">
                              manage
                            </v-list-subheader>
                            <v-list-item
                              v-if="hasPermission('manageInstance', sub.id)"
                              :to="`/manage/${sub.id}/instance`"
                              prepend-icon="$mdiCog"
                              title="INSTANCE"
                              data-manage-instance
                            />
                            <v-list-item
                              v-if="hasPermission('manageSubscription', sub.id) && !sub.hasPartner"
                              :to="`/manage/${sub.id}/subscription`"
                              prepend-icon="$mdiWalletMembership"
                              title="SUBSCRIPTION"
                              data-manage-subscription
                            />
                          </v-list>
                        </v-menu>
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </c-instance>
              </c-pricing>
            </c-region>
          </c-subscription>
        </div>
        <v-card
          v-else
          class="text-center"
          data-test-no-subscription
        >
          <v-card-item>
            <v-card-title class="text-uppercase">
              You don't have any subscriptions
            </v-card-title>
          </v-card-item>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn
              :loading="subscriptionLoading || isNavigatingToTrial"
              variant="elevated"
              @click="navigateToTrial"
            >
              Start free Trial now!
            </v-btn>
            <v-spacer />
          </v-card-actions>
        </v-card>
      </p-fetching-indicator>
    </c-subscription>
  </v-container>
</template>

<style scoped lang="scss">
$invisible: rgb(0 0 0 / 0%);

.loading::after {
  content: '.';
  animation: dots 1s steps(5, end) infinite;
}

@keyframes dots {
  0%,
  20% {
    color: $invisible;
    text-shadow: 0.25em 0 0 $invisible, 0.5em 0 0 $invisible;
  }

  40% {
    color: rgb(var(--v-theme-secondary));
    text-shadow: 0.25em 0 0 $invisible, 0.5em 0 0 $invisible;
  }

  60% {
    text-shadow: 0.25em 0 0 rgb(var(--v-theme-secondary)), 0.5em 0 0 $invisible;
  }

  80%,
  100% {
    text-shadow: 0.25em 0 0 rgb(var(--v-theme-secondary)), 0.5em 0 0 rgb(var(--v-theme-secondary));
  }
}
</style>
